/**
 * Auth boundary. The installer talks to the cloud with a bearer token; where that token
 * comes from is behind AuthProvider.
 *
 * Intended production integration (documented in docs/cloud-api-contract.md §Auth):
 * QuickHub's existing login (review-api JWT) via OAuth 2.1 authorization-code + PKCE at
 * `${authBase}/oauth/authorize`, redirect back to install.quickhub.ai/callback, exchange at
 * `POST ${apiBase}/installer/auth/token`. The resulting JWT carries `business_id`; the cloud
 * scopes lines/devices by it. Until that endpoint exists, DevBearerAuth accepts a pasted
 * token (lab + UAT).
 */
export interface AuthProvider {
  readonly kind: "dev-bearer" | "quickhub-pkce";
  /** Current bearer token, or undefined when signed out. */
  getToken(): Promise<string | undefined>;
  /** Start sign-in. May navigate away (PKCE) or resolve immediately (dev). */
  signIn(input?: { token?: string }): Promise<void>;
  signOut(): Promise<void>;
}

const DEV_KEY = "qh-installer:dev-token";

/**
 * Sign-in handed over by the QuickHub portal (review-api `createVoiceLineInstallerLink`).
 *
 * The portal opens `…/#token=<jwt>`: a one-hour installer token for the business,
 * issued after it accepted the Voice Line terms. It arrives in the URL FRAGMENT so it
 * never reaches a server or its logs. Taken once, stored like a pasted token, and the
 * fragment is wiped from the address bar and history so it is not bookmarked, shared
 * or left in a screenshot.
 *
 * Returns true when a token was taken.
 */
export function takeTokenFromFragment(
  auth: Pick<AuthProvider, "signIn">,
  loc: Pick<Location, "hash" | "pathname" | "search">,
  history: Pick<History, "replaceState">,
): boolean {
  const params = new URLSearchParams(loc.hash.replace(/^#/, ""));
  const token = params.get("token")?.trim();
  if (!token) return false;
  params.delete("token");
  const rest = params.toString();
  history.replaceState(null, "", `${loc.pathname}${loc.search}${rest ? `#${rest}` : ""}`);
  void auth.signIn({ token });
  return true;
}

export class DevBearerAuth implements AuthProvider {
  readonly kind = "dev-bearer" as const;
  constructor(private readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem">) {}
  getToken(): Promise<string | undefined> {
    return Promise.resolve(this.storage.getItem(DEV_KEY) ?? undefined);
  }
  signIn(input?: { token?: string }): Promise<void> {
    const t = input?.token?.trim();
    if (!t) return Promise.reject(new Error("token required"));
    this.storage.setItem(DEV_KEY, t);
    return Promise.resolve();
  }
  signOut(): Promise<void> {
    this.storage.removeItem(DEV_KEY);
    return Promise.resolve();
  }
}

export interface PkceConfig {
  authorizeUrl: string;
  tokenUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

function b64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * OAuth 2.1 authorization-code + PKCE against QuickHub auth. Functional skeleton; the
 * cloud side (`/installer/auth/token`) is in the API contract but not yet implemented.
 */
export class QuickHubPkceAuth implements AuthProvider {
  readonly kind = "quickhub-pkce" as const;
  private static readonly TOKEN = "qh-installer:access-token";
  private static readonly VERIFIER = "qh-installer:pkce-verifier";

  constructor(
    private readonly cfg: PkceConfig,
    private readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
    private readonly navigate: (url: string) => void,
    private readonly fetchFn: typeof fetch = fetch,
  ) {}

  getToken(): Promise<string | undefined> {
    return Promise.resolve(this.storage.getItem(QuickHubPkceAuth.TOKEN) ?? undefined);
  }

  async signIn(): Promise<void> {
    const verifierBytes = crypto.getRandomValues(new Uint8Array(32));
    const verifier = b64url(verifierBytes);
    const challenge = b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
    this.storage.setItem(QuickHubPkceAuth.VERIFIER, verifier);
    const u = new URL(this.cfg.authorizeUrl);
    u.searchParams.set("response_type", "code");
    u.searchParams.set("client_id", this.cfg.clientId);
    u.searchParams.set("redirect_uri", this.cfg.redirectUri);
    u.searchParams.set("scope", this.cfg.scope);
    u.searchParams.set("code_challenge", challenge);
    u.searchParams.set("code_challenge_method", "S256");
    this.navigate(u.toString());
  }

  /** Call on /callback?code=… */
  async handleCallback(code: string): Promise<void> {
    const verifier = this.storage.getItem(QuickHubPkceAuth.VERIFIER);
    if (!verifier) throw new Error("no PKCE verifier in storage");
    const res = await this.fetchFn(this.cfg.tokenUrl, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ grant_type: "authorization_code", code, redirect_uri: this.cfg.redirectUri, client_id: this.cfg.clientId, code_verifier: verifier }).toString(),
    });
    if (!res.ok) throw new Error(`token exchange failed: HTTP ${res.status}`);
    const body = (await res.json()) as { access_token?: string };
    if (!body.access_token) throw new Error("token exchange returned no access_token");
    this.storage.removeItem(QuickHubPkceAuth.VERIFIER);
    this.storage.setItem(QuickHubPkceAuth.TOKEN, body.access_token);
  }

  signOut(): Promise<void> {
    this.storage.removeItem(QuickHubPkceAuth.TOKEN);
    return Promise.resolve();
  }
}
